import {
  Theme,
  radius,
  rem,
  spacing,
  status,
  token,
} from '@thiememeulenhoff/lit-sdk';
import { css, CSSResult, unsafeCSS } from 'lit';
import { tagName } from '../../helpers';
import { LabelVariant } from './label.types';

export const tag = tagName('label');
const theme = new Theme(tag);

const labelVariantTokens: Record<
  LabelVariant,
  { background: CSSResult; foreground: CSSResult; border: CSSResult }
> = {
  neutral: {
    background: token('background-tertiary'),
    foreground: token('foreground-secondary'),
    border: token('border-subtle'),
  },
  success: {
    background: status('success', 'background'),
    foreground: status('success', 'foreground'),
    border: status('success', 'border'),
  },
  warning: {
    background: status('warning', 'background'),
    foreground: status('warning', 'foreground'),
    border: status('warning', 'border'),
  },
  weergave: {
    background: token('albert-label-weergave-background-default'),
    foreground: token('albert-label-weergave-foreground-default'),
    border: token('albert-label-weergave-border-default'),
  },
  info: {
    background: status('info', 'background'),
    foreground: status('info', 'foreground'),
    border: status('info', 'border'),
  },
};

const labelVariant = (labelVariant: LabelVariant) => {
  const selector = unsafeCSS(`[variant=${labelVariant}]`);
  const labelTokens = labelVariantTokens[labelVariant];

  return css`
    :host(${selector}) {
      ${theme.props([
        ['background', `${labelVariant}-background`, labelTokens.background],
        ['color', `${labelVariant}-color`, labelTokens.foreground],
        [
          'border',
          `${labelVariant}-border`,
          `${labelTokens.border} solid ${rem(1)}`,
        ],
      ])};
    }

    :host(${selector}[disable-border]) {
      border: 0;
    }
  `;
};

export const label = css`
  :host {
    align-items: center;
    block-size: ${rem(32)};
    border-radius: ${radius('extra-large')};
    display: inline-flex;
    gap: ${spacing(100)};
    justify-content: center;
    padding: ${spacing(100, 200)};
    text-align: center;
  }

  :host([size='small']) {
    block-size: ${rem(24)};
    font-size: ${rem(14)};
    font-weight: 600;
    padding: ${spacing(0, 150)};
  }

  :host([size='medium']) {
    font-size: ${rem(16)};
    padding: ${spacing(100, 200)};
  }

  ${labelVariant('neutral')}
  ${labelVariant('success')}
  ${labelVariant('warning')}
  ${labelVariant('weergave')}
  ${labelVariant('info')}
`;
